import React from 'react';
import styled from 'styled-components';
import { ModalShell } from '../ui/shell';
import { Btn } from '../ui/primitives';

const EmptyStateBody = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const VideoFrame = styled.iframe`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  max-height: 78vh;
  border: 0;
  border-radius: ${({ theme }) => theme.rMd};
  background: #000;
`;

const VideoPlayer = styled.video`
  display: block;
  width: 100%;
  height: auto;
  max-height: 78vh;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.rMd};
  background: #000;
`;

/**
 * Detects a direct video file (vs. an embeddable page like YouTube/Vimeo).
 * Direct files must be rendered in a <video> element — an <iframe> would let the
 * browser download them or open a new tab depending on the server headers.
 */
const isDirectVideoFile = (url) => {
  const VIDEO_EXT = /\.(mp4|webm|ogg|ogv|mov|m4v|mkv)$/;
  try {
    return VIDEO_EXT.test(new URL(url, window.location.origin).pathname.toLowerCase());
  } catch {
    return /\.(mp4|webm|ogg|ogv|mov|m4v|mkv)(\?|#|$)/i.test(url || '');
  }
};

/**
 * Training video modal — plays the video inline (HTML5 player for direct files,
 * embedded iframe for hosted players) or an empty-state when no URL exists.
 */
const TrainingVideoDialog = ({ open, videoUrl, onClose }) => {
  if (!open) return null;

  const hasVideo = Boolean(videoUrl);
  const playsDirectFile = hasVideo && isDirectVideoFile(videoUrl);

  return (
    <ModalShell
      title={hasVideo ? 'Play Video' : 'No Video Found'}
      onClose={onClose}
      max={hasVideo ? 'min(1120px, 94vw)' : '480px'}
      foot={(
        <Btn type="button" $variant="ghost" onClick={onClose}>
          Close
        </Btn>
      )}
    >
      {hasVideo ? (
        playsDirectFile ? (
          <VideoPlayer
            src={videoUrl}
            controls
            autoPlay
            controlsList="nodownload"
            title="Training Video"
          />
        ) : (
          <VideoFrame
            src={videoUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Training Video"
          />
        )
      ) : (
        <EmptyStateBody>No video found for this training.</EmptyStateBody>
      )}
    </ModalShell>
  );
};

export default TrainingVideoDialog;
